// apps/web/src/lib/coletor/celulas-coletor.ts — células reais do coletor (UAT 2026-10-06).
// A fila do coletor escolhe a célula entre as células ativas do cadastro, priorizando as
// células vinculadas a este tablet (vínculo do Kanban do operador).
import type { KanbanTabletQuadro } from '@pieltec/contracts';
import { apiFetch } from '../csrf-fetch';
import { cabecalhosDispositivo, obterCredencial, obterQuadroCache } from '../kanban/dispositivo';
import type { ObterSessaoResult } from '../session';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export interface CelulaColetor {
  readonly codigo: string;
  readonly nome: string;
}

export interface OpcoesCelulaColetor {
  /** Células vinculadas a este tablet (ativas), na ordem do vínculo. */
  readonly vinculadas: readonly CelulaColetor[];
  /** Demais células ativas, para quem precisa consultar outro setor. */
  readonly outras: readonly CelulaColetor[];
  /** Célula selecionada por padrão: a primeira vinculada; sem vínculo, a primeira ativa. */
  readonly padrao: string | null;
}

export const PERMISSAO_SOLICITAR_MATERIAL = 'estoque:solicitar';

/** Rótulo mostrado ao operador: código · nome (sem identificadores internos). */
export function rotuloCelula(celula: CelulaColetor): string {
  const nome = celula.nome.trim();
  return nome === '' || nome === celula.codigo ? celula.codigo : `${celula.codigo} · ${nome}`;
}

/**
 * Monta as opções do seletor. Com a lista de ativas disponível, as vinculadas são filtradas
 * por ela (célula desativada some do tablet). Sem a lista (sem permissão de cadastro ou falha),
 * o vínculo do tablet é a única fonte confiável.
 */
export function montarOpcoesCelulas(
  ativas: readonly CelulaColetor[] | null,
  vinculadas: readonly CelulaColetor[],
): OpcoesCelulaColetor {
  const porCodigo = new Map((ativas ?? []).map((celula) => [celula.codigo, celula]));
  const vistas = new Set<string>();
  const doTablet: CelulaColetor[] = [];
  for (const celula of vinculadas) {
    if (vistas.has(celula.codigo)) continue;
    if (ativas !== null && !porCodigo.has(celula.codigo)) continue;
    vistas.add(celula.codigo);
    doTablet.push(porCodigo.get(celula.codigo) ?? celula);
  }
  const outras = (ativas ?? []).filter((celula) => !vistas.has(celula.codigo));
  return { vinculadas: doTablet, outras, padrao: doTablet[0]?.codigo ?? outras[0]?.codigo ?? null };
}

export interface DependenciasCelulasColetor {
  /** Células ativas do cadastro (`GET /api/v1/planejamento/celulas`). */
  readonly listarAtivas: () => Promise<readonly CelulaColetor[]>;
  /** Quadro do Kanban deste tablet; `null` quando o tablet não está vinculado. */
  readonly quadroDoTablet: () => Promise<Pick<KanbanTabletQuadro, 'celulas'> | null>;
}

/** Carrega ativas e vinculadas em paralelo; a falha de uma fonte não derruba a outra. */
export async function carregarOpcoesCelulas(deps: DependenciasCelulasColetor): Promise<OpcoesCelulaColetor> {
  const [ativas, quadro] = await Promise.allSettled([deps.listarAtivas(), deps.quadroDoTablet()]);
  const vinculadas = quadro.status === 'fulfilled' && quadro.value
    ? quadro.value.celulas.map(({ codigo, nome }) => ({ codigo, nome }))
    : [];
  return montarOpcoesCelulas(ativas.status === 'fulfilled' ? ativas.value : null, vinculadas);
}

/** Fontes reais: cadastro de células (mesmo cliente da configuração do tablet) e quadro do vínculo. */
export const dependenciasCelulasColetor: DependenciasCelulasColetor = {
  listarAtivas: async () => {
    const res = await apiFetch<{ data?: readonly CelulaColetor[] }>('/api/v1/planejamento/celulas');
    return (res.data ?? []).map(({ codigo, nome }) => ({ codigo, nome }));
  },
  quadroDoTablet: async () => {
    const credencial = await obterCredencial();
    if (!credencial) return null;
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/kanban/tablet/quadro`, { credentials: 'include', headers: cabecalhosDispositivo(credencial) });
      if (res.ok) return (await res.json()) as KanbanTabletQuadro;
    } catch {
      // Sem rede: o último quadro salvo no tablet ainda diz quais são as células dele.
    }
    return obterQuadroCache();
  },
};

/** Ação de pedir material exige `estoque:solicitar`; sem sessão, a ação fica oculta. */
export function podeSolicitarMaterial(sessao: ObterSessaoResult): boolean {
  return sessao.autenticado && sessao.usuario.permissions.includes(PERMISSAO_SOLICITAR_MATERIAL);
}
